import { useState, type JSX } from 'react'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { FormField } from './FormField.js'

/**
 * PasswordField — a labelled password input with a show/hide toggle and an
 * optional "Generate" button, matching the account-creation password pattern
 * used across the app. Visibility state is self-contained; the password value
 * itself stays fully controlled by the caller.
 */
export function PasswordField({
  id,
  label = 'Password',
  required = false,
  error,
  help,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  hideActions = false,
  onGenerate,
}: {
  id: string
  label?: string | undefined
  required?: boolean | undefined
  error?: string | undefined
  help?: string | undefined
  value: string
  onChange: (value: string) => void
  onBlur?: (() => void) | undefined
  placeholder?: string | undefined
  disabled?: boolean | undefined
  /** Hide the show/hide + generate buttons (e.g. in a view-only form). */
  hideActions?: boolean | undefined
  /** When provided, shows a "Generate" button that fills the field with the returned password. */
  onGenerate?: (() => string) | undefined
}): JSX.Element {
  const [visible, setVisible] = useState(false)

  return (
    <FormField label={label} htmlFor={id} required={required} error={error} help={help}>
      <div className="flex gap-2">
        <InputText
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`flex-1 ${error ? 'p-invalid' : ''}`}
        />
        {!hideActions && (
          <>
            <Button
              type="button"
              icon={visible ? 'pi pi-eye-slash' : 'pi pi-eye'}
              aria-label={visible ? 'Hide password' : 'Show password'}
              className="p-button-outlined"
              onClick={() => setVisible((v) => !v)}
              disabled={disabled}
              title={visible ? 'Hide password' : 'Show password'}
            />
            {onGenerate && (
              <Button
                type="button"
                label="Generate"
                icon="pi pi-refresh"
                className="p-button-outlined"
                onClick={() => {
                  onChange(onGenerate())
                  setVisible(true)
                }}
                disabled={disabled}
              />
            )}
          </>
        )}
      </div>
    </FormField>
  )
}
